import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import type { AnalyticsSession } from '@sia/analytics-contracts';
import { Icon, type IconName } from '@/components/icon';
import { logout } from '@/lib/api';
import { plannedSections, sections } from '@/lib/sections';

const sectionIcons: Record<string, IconName> = {
  overview: 'home', 'global-usage': 'trends', 'average-per-user': 'users',
  'preference-profiles': 'settings', 'listing-inventory': 'layers',
  'alerts-engagement': 'alerts', 'scraper-health': 'vitality',
};

export function AppShell({ session, children }: PropsWithChildren<{ session: Extract<AnalyticsSession, { authenticated: true }> }>) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [compactNavigation, setCompactNavigation] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 900px)').matches
      : false,
  );
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setCompactNavigation(event.matches);
      if (!event.matches) setDrawerOpen(false);
    };

    setCompactNavigation(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!compactNavigation || !drawerOpen) return;

    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDrawerOpen(false);
      menuButtonRef.current?.focus();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [compactNavigation, drawerOpen]);

  async function signOut() {
    try {
      await logout();
    } finally {
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'analytics-session' });
      queryClient.setQueryData<AnalyticsSession>(['analytics-session'], { authenticated: false });
      navigate('/sign-in', { replace: true });
    }
  }

  return <div className="app-shell">
    <button ref={menuButtonRef} className="mobile-menu" aria-label="Open navigation" aria-expanded={drawerOpen} aria-controls="analytics-navigation" onClick={() => setDrawerOpen(true)}><Icon name="menu" /></button>
    {drawerOpen ? <button className="drawer-scrim" aria-label="Close navigation" onClick={() => setDrawerOpen(false)} /> : null}
    <aside
      id="analytics-navigation"
      className={`sidebar${drawerOpen ? ' is-open' : ''}`}
      aria-hidden={compactNavigation && !drawerOpen}
      inert={compactNavigation && !drawerOpen}
    >
      <div className="brand-row">
        <img src="/images/sia-logo.png" alt="Sia" />
        <span>Analytics admin</span>
        <button ref={closeButtonRef} className="sidebar-close" onClick={() => setDrawerOpen(false)} aria-label="Close navigation"><Icon name="close" /></button>
      </div>
      <nav aria-label="Analytics navigation">
        <p className="nav-label">Traction</p>
        <NavLink to="/" end onClick={() => setDrawerOpen(false)}>
          <Icon name="home" size={17} /><span>Overview</span>
        </NavLink>
        <p className="nav-label">Operate</p>
        {sections.slice(1).map((section) => <NavLink to={section.path} key={section.id} onClick={() => setDrawerOpen(false)}>
          <Icon name={sectionIcons[section.id]} size={17} /><span>{section.shortTitle}</span>
        </NavLink>)}
        <p className="nav-label">Planned</p>
        {plannedSections.map((section, index) => <NavLink to={section.path} key={section.path} onClick={() => setDrawerOpen(false)}>
          <Icon name={index === 0 ? 'sparkles' : index === 1 ? 'search' : 'data'} size={17} /><span>{section.title}</span>
        </NavLink>)}
      </nav>
      <button className="admin-profile" onClick={() => void signOut()} aria-label="Sign out">
        <span className="avatar">{session.user.email.slice(0, 2).toUpperCase()}</span>
        <span><strong>{session.user.email.split('@')[0]}</strong><small>Admin access · Sign out</small></span>
      </button>
    </aside>
    <main>{children}</main>
  </div>;
}
